import React, { useEffect, useContext } from "react";
import { useState } from "react";
import { Box, Button, Card, CardContent, Typography, TextField, Table, TableCell, TableBody, TableHead, TableRow, Radio, RadioGroup, IconButton, Drawer, FormControlLabel, checked, Checkbox, TableContainer } from "@mui/material";
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import { SearchIcon } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { manualDetails } from "../../../services/webServices";
import { generateTestCasesForAPI, swaggerToPostman, APITraceabilityMatrix, endpoitnExtractionAPI, resourceChainTestCases } from "../../../services/webServices";
import { handleProgressBar } from "../../custom-components/ProgressBar/ProgressBarWithAPI.js";
import { LoadingModalContext } from "../../main-pages/ServicePage";
import MessagePopup from "../../popup-components/MessagePopup";
import { Close, ArrowBack } from '@mui/icons-material';





function EndpointsDisplayPage() {

    const location = useLocation();
    const navigate = useNavigate();
    const { setLoadingState } = useContext(LoadingModalContext);


    const [search, setSearch] = useState("");
    const [overAllEndpoints, setOverAllEndpoints] = useState([]);
    const [displayPageContent, setDisplayPageContent] = useState([])
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupMessage, setPopupMessage] = useState("");
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectionMode, setSelectionMode] = useState('choose');
    const [selectedTestCases, setSelectedTestCases] = useState([]);

    useEffect(() => {

        if (location.state?.overAllEndpointExtractedData) {

            const flattenedEndpoints = location.state.overAllEndpointExtractedData
                .flatMap(item =>
                    Object.values(item)
                        .filter(value => Array.isArray(value))
                        .flat()
                );

            setOverAllEndpoints(flattenedEndpoints);
        }
        if (location.state?.displayPageContent) {
            setDisplayPageContent(location.state.displayPageContent);
        }

    }, [location.state?.overAllEndpointExtractedData, location.state?.displayPageContent])

    function flattenPostmanItems(items) {
        const flat = [];

        function recurse(subItems) {
            subItems.forEach(item => {
                if (item.request) {
                    flat.push(item); // it's a request
                } else if (item.item && item.item.length > 0) {
                    recurse(item.item); // it's a folder
                }
            });
        }
        recurse(items);
        return flat;
    }


    const filteredRows = overAllEndpoints.filter((row) =>
        Object.values(row).some((val) =>
            (val?.toString() ?? '').toLowerCase().includes(search.toLowerCase())
        )
    );

    const functionalTestCases = [
        { id: 'Positive test case', label: 'Positive test cases' },
        { id: 'Negative test case', label: 'Negative test cases' },
        { id: 'Error Handling test case', label: 'Error handling test cases' },
        { id: 'Boundary test case', label: 'Boundary test cases' },
        { id: 'Resource Chain test case', label: 'End-End testing(Resource-Chain)' },
    ];

    const nonFunctionalTestCases = [
        { id: 'Performance test case', label: 'Performance test cases' },
        { id: 'Security test case', label: 'Security test cases' },

    ];

    const handleTestCaseToggle = (testCaseId) => {
        setSelectedTestCases(prev =>
            prev.includes(testCaseId)
                ? prev.filter(id => id !== testCaseId)
                : [...prev, testCaseId]
        );
    };

    const handleSelectionModeChange = (event) => {
        setSelectionMode(event.target.value);
        if (event.target.value === 'all') {
            setSelectedTestCases([]);
        }
    };



    const handleBack = () => {
        setSelectedTestCases([]);
        setSelectionMode('choose');
        setDrawerOpen(false)
    };


    const renderInputData = (inputData) => {
        if (!inputData || Object.keys(inputData).length === 0) {
            return "-";
        }
        if (typeof inputData === 'object') {
            return JSON.stringify(inputData, null, 2);
        }
        return inputData;
    };

    const generateTestcases = async () => {
        let allTestCasesEnd = []; // Variable to append all outputs
        let tcCounter = 1;


        const testCasesToGenerate = selectionMode === 'all'
            ? [
                "Positive test case", "Negative test case", "Boundary test case", "Error Handling test case",
                "Security test case", "Performance test case", "Resource Chain test case"]
            : selectedTestCases;


        const isOnlyResourceChain =
            selectedTestCases.length === 1 && selectedTestCases[0] === "Resource Chain test case";

        if (isOnlyResourceChain) {
            try {
                let progressHandler;
                progressHandler = handleProgressBar({ setLoadingState, totalDurationMs: 120000 });
                progressHandler.start("Generating Resource Chain Test Cases");

                // Combine all endpoints from available sources
                const allEndpoints = [];
           

                for (const content of displayPageContent) {

                    if (content.uploadedSpecification?.data) {
                        const stringdata = JSON.stringify(content.uploadedSpecification.data);
                        allEndpoints.push(stringdata);
                    }
                     if (content?.swaggerData) {
                        allEndpoints.push(content.swaggerData.spec);
                    }
                     if (content.manualDetailsData?.spec?.data) {
                        allEndpoints.push(content.manualDetailsData.spec.data);
                    }
                }


                if (allEndpoints.length === 0) {
                    console.warn(" No valid endpoints found for resource chain test case generation.");
                    setPopupMessage("No valid endpoints found to generate resource chain test cases.");
                    setPopupOpen(true);
                    return;
                }

                // Call the Resource Chain API directly
                const resourceChainResponse = await resourceChainTestCases({ endpoints: allEndpoints });
                if (resourceChainResponse?.data?.length > 0) {
                    allTestCasesEnd.push(...resourceChainResponse.data);
                }
                await progressHandler.finishAndClose();

                // Navigate to test case display page
                navigate("/dashboard/api-testing/test-cases-display", {
                    state: {
                        ...location.state,
                        generateTestCasesData: allTestCasesEnd,
                        button: "importAPIDefinition",
                        testCasesToGenerate: testCasesToGenerate
                    },
                });
            } catch (error) {
                console.error(" Error generating resource chain test cases:", error);
                setPopupMessage("Something went wrong while generating Resource Chain test cases.");
                setPopupOpen(true);
            } finally {
                setLoadingState({ open: false, message: "", progress: null });
            }

            // Stop further execution
            return;
        }

        for (const content of displayPageContent) {
            if (content.uploadedSpecification) {
                const filecontent = content.uploadedSpecification.data;

                const fileName = content.uploadedSpecification.filedata.name;
                const fileExtension = fileName
                    .slice(fileName.lastIndexOf("."))
                    .toLowerCase();
                let progressHandler;
                progressHandler = handleProgressBar({ setLoadingState, totalDurationMs: 216000 });
                progressHandler.start("Generating test cases from Postman Collection file");


                // let tcCounter = 1; // global test case ID counter
                const finalArray = [];
                let traceabilityMatrix = null
                try {

                    if (content.uploadedSpecification.data && content.uploadedSpecification.data.length > 0 && (fileExtension === ".json" || fileExtension === ".postman_collection")) {
                        try {

                            const promise = filecontent.map((item) => { return generateTestCasesForAPI({ item: item, testCasesToGenerate: testCasesToGenerate }) })
                            const results = await Promise.allSettled(promise);
                            const allTestCases = [];
                            results.map((result) => {
                                if (result.status === "fulfilled" && result.value?.data?.testcases) {
                                    allTestCases.push(...result.value.data.testcases);
                                } else {
                                    console.error("One call failed:", result.reason);
                                }
                            })


                            if (allTestCases && allTestCases.length > 0) {
                                const updatedTestCases = allTestCases.map(testcase => {
                                    // const isFunctional = testcase.Category?.toLowerCase().includes("functional");
                                    const isFunctional = testcase.Category?.toLowerCase() === "functional testing";

                                    const subCategory = testcase["Sub-Category"]?.toLowerCase() || "";

                                    let prefix = isFunctional ? "TCD_FT" : "TCD_NFT";
                                    let shortCode = "";

                                    if (subCategory.includes("positive")) shortCode = "POS";
                                    else if (subCategory.includes("negative")) shortCode = "NEG";
                                    else if (subCategory.includes("boundary")) shortCode = "BOUND";
                                    else if (subCategory.includes("error")) shortCode = "ERR";
                                    else if (subCategory.includes("security")) shortCode = "SEC";
                                    else if (subCategory.includes("performance")) shortCode = "PERF";

                                    const padded = String(tcCounter).padStart(2, "0");
                                    const id = `${prefix}_${padded}_${shortCode}`;
                                    tcCounter++; // increment global counter

                                    return {
                                        ...testcase,
                                        "Test Case ID": id
                                    };
                                });

                                finalArray.push(...updatedTestCases);
                            }
                            if (selectedTestCases.includes("Resource Chain test case") || selectionMode === 'all') {
                                progressHandler.updateMessage("Generating Resource Chain Test Cases");
                                const resourceChainResponse = await resourceChainTestCases({ endpoints: filecontent });
                                if (resourceChainResponse.data && resourceChainResponse.data.length > 0) {
                                    finalArray.push(...resourceChainResponse.data);

                                }
                            }
                            allTestCasesEnd.push(...finalArray);
                        } catch (error) {
                            console.error(
                                error?.response?.data?.error || error.message,
                                "Error processing item:"
                            );
                            // setPopupMessage("Error processing some test cases. Please check the file format and try again.");
                            // setPopupOpen(true);
                        }

                        // }
                    } else {
                        // yml flow
                        try {
                            progressHandler = handleProgressBar({ setLoadingState, totalDurationMs: 195000 })

                            progressHandler.start("Generating test cases from swagger file");

                            const responseData = await swaggerToPostman(filecontent);


                            if (responseData.data.item) {
                                const flattenedItems = flattenPostmanItems(responseData.data.item);

                                const promises = flattenedItems.map(async (item, i) => {


                                    try {
                                        const testcasesresponse = await generateTestCasesForAPI({ item: item, testCasesToGenerate: testCasesToGenerate });



                                        // finalArray.push(...testcasesresponse.data.testcases);
                                        if (testcasesresponse.data && testcasesresponse.data?.testcases) {

                                            return testcasesresponse.data.testcases;
                                        }




                                    } catch (error) {
                                        console.error(
                                            error?.response?.data?.error || error.message,
                                            "Error processing swagger item:", i
                                        );
                                        // setPopupMessage("Error processing some test cases. Please check the file format and try again.");
                                        // setPopupOpen(true);
                                    }
                                    return [];
                                }
                                );
                                const results = await Promise.allSettled(promises);
                                for (const result of results) {
                                    if (result.status === "fulfilled" && Array.isArray(result.value)) {
                                        const updatedTestCases = result.value.map(testcase => {
                                            // const isFunctional = testcase.Category?.toLowerCase().includes("functional");
                                            const isFunctional = testcase.Category?.toLowerCase() === "functional testing";
                                            const subCategory = testcase["Sub-Category"]?.toLowerCase() || "";
                                            let prefix = isFunctional ? "TCD_FT" : "TCD_NFT";
                                            let shortCode = "";
                                            if (subCategory.includes("positive")) shortCode = "POS";
                                            else if (subCategory.includes("negative")) shortCode = "NEG";
                                            else if (subCategory.includes("boundary")) shortCode = "BOUND";
                                            else if (subCategory.includes("error")) shortCode = "ERR";
                                            else if (subCategory.includes("security")) shortCode = "SEC";

                                            const padded = String(tcCounter).padStart(2, "0");
                                            const id = `${prefix}_${padded}_${shortCode}`;
                                            tcCounter++;
                                            return {
                                                ...testcase,
                                                "Test Case ID": id
                                            };
                                        });
                                        finalArray.push(...updatedTestCases);
                                    }
                                }

                                if (selectedTestCases.includes("Resource Chain test case") || selectionMode === "all") {
                                    progressHandler.updateMessage("Generating Resource Chain Test Cases");

                                    try {
                                        const resourceChainResponse = await resourceChainTestCases({
                                            endpoints: filecontent
                                        });

                                        if (resourceChainResponse?.data?.length) {
                                            finalArray.push(...resourceChainResponse.data);
                                        }
                                    } catch (chainError) {
                                        console.error(" Error generating resource chain test cases:", chainError.message);
                                    }

                                }
                                allTestCasesEnd.push(...finalArray);






                            }
                        } catch (error) {
                            console.error(
                                error?.response?.data?.error || error.message,
                                "Error converting swagger to postman"
                            );
                            setPopupMessage("Error converting swagger to postman. Please try again.");
                            setPopupOpen(true);
                        }
                    }

                    await progressHandler.finishAndClose();

                } catch (error) {
                    console.error("Error generating test cases:", error);
                    setLoadingState({ open: false, message: "", progress: null });
                    setPopupMessage("Something went wrong while generating test cases.");
                    setPopupOpen(true);
                } finally {
                    if (progressHandler) {
                        progressHandler.stop();
                    }
                    setLoadingState({ open: false, message: "", progress: null });
                }



            }
            if (content.swaggerData) {
                let progressHandler;
                try {

                    const finalArray = [];
                    let traceabilityMatrix = null;
                    // const responseParsedData = await htmlParsingForSwagger({ url: apiurl });
                    // if (responseParsedData && responseParsedData.data) {
                    //   // Handle the parsed data as needed
                    //   setOpenAPISpec(responseParsedData.data.spec);
                    progressHandler = handleProgressBar({ setLoadingState, totalDurationMs: 420000 })

                    const responsePostmanCollection = await swaggerToPostman(content.swaggerData.spec);
                    if (responsePostmanCollection.data.item) {
                        const flattenedItems = flattenPostmanItems(responsePostmanCollection.data.item);
                        if (flattenedItems.length == 1) {
                            progressHandler.start("Generating Test Cases");
                        }
                        else {
                            progressHandler.start("Generating test cases from swagger data ");
                        }

                        const promises = flattenedItems.map(async (item, index) => {
                            progressHandler.updateMessage(`Generating test cases for swagger data, endpoints ${index + 1}`);
                            try {
                                const testcasesresponse = await generateTestCasesForAPI({ item: item, testCasesToGenerate: testCasesToGenerate });

                                if (testcasesresponse.data && testcasesresponse.data.testcases) {

                                    return testcasesresponse.data.testcases;

                                }
                            } catch (error) {
                                console.error(
                                    error?.response?.data?.error || error.message,
                                    "Error processing swagger item:", index
                                );
                            }
                            return [];
                        }
                        );
                        const results = await Promise.allSettled(promises);
                        for (const result of results) {
                            if (result.status === "fulfilled" && Array.isArray(result.value)) {
                                const updated = result.value.map((testcase) => {
                                    const isFunctional = testcase.Category?.toLowerCase() === "functional testing";
                                    const subCategory = testcase["Sub-Category"]?.toLowerCase() || "";

                                    let prefix = isFunctional ? "TCD_FT" : "TCD_NFT";
                                    let shortCode = "";

                                    if (subCategory.includes("positive")) shortCode = "POS";
                                    else if (subCategory.includes("negative")) shortCode = "NEG";
                                    else if (subCategory.includes("boundary")) shortCode = "BOUND";
                                    else if (subCategory.includes("error")) shortCode = "ERR";
                                    else if (subCategory.includes("security")) shortCode = "SEC";
                                    else if (subCategory.includes("performance")) shortCode = "PERF";

                                    const padded = String(tcCounter).padStart(2, "0");
                                    const id = `${prefix}_${padded}_${shortCode}`;
                                    tcCounter++;

                                    return {
                                        ...testcase,
                                        "Test Case ID": id,
                                    };
                                });

                                finalArray.push(...updated);
                            }
                        }

                        if (selectedTestCases.includes("Resource Chain test case") || selectionMode === "all") {
                            progressHandler.updateMessage("Generating Resource Chain Test Cases");

                            try {
                                const resourceChainResponse = await resourceChainTestCases({
                                    endpoints: responsePostmanCollection.data.item
                                });

                                if (resourceChainResponse?.data?.length) {
                                    finalArray.push(...resourceChainResponse.data);
                                }
                            } catch (chainError) {
                                console.error(" Error generating resource chain test cases:", chainError.message);
                            }

                        }


                        allTestCasesEnd.push(...finalArray);
                        await progressHandler.finishAndClose();



                    }
                } catch (error) {
                    console.error("Error generating test cases:", error);
                    setLoadingState({ open: false, message: "", progress: null });
                    setPopupMessage("Something went wrong while generating test cases.");
                    setPopupOpen(true);
                } finally {

                    if (progressHandler) {
                        progressHandler.stop();
                        setLoadingState({ open: false, message: "", progress: null });
                    }
                }
            }
            if (content.manualDetailsData) {


                //    let tcCounter = 1; // Ensure tcCounter is initialized

                let progressHandler;
                progressHandler = handleProgressBar({ setLoadingState, totalDurationMs: 195000 });
                try {

                    const finalArray = [];
                    let traceabilityMatrix = null;
                    progressHandler.start(`Generating test cases from API endpoints`);

                    const responseData = await swaggerToPostman(content.manualDetailsData.spec.data);

                    if (responseData.data.item) {
                        const flattenedItems = flattenPostmanItems(responseData.data.item);


                        const promises = flattenedItems.map(async (item, index) => {


                            try {
                                const testcasesresponse = await generateTestCasesForAPI({ item: item, testCasesToGenerate: testCasesToGenerate });

                                if (testcasesresponse.data && testcasesresponse.data?.testcases) {

                                    return testcasesresponse.data.testcases;
                                }
                            } catch (error) {
                                console.error(
                                    error?.response?.data?.error || error.message,
                                    "Error processing swagger item:", index + 1
                                );
                                // setPopupMessage(`Error processing item ${i + 1}: ${error?.response?.data?.error || error.message}`);
                                // setPopupOpen(true);
                            }
                            return []; // Return an empty array if there's an error
                        }

                        );



                        const results = await Promise.allSettled(promises);

                        // Flatten results and assign Test Case IDs safely
                        for (const result of results) {
                            if (result.status === "fulfilled" && Array.isArray(result.value)) {
                                const updated = result.value.map((testcase) => {
                                    const isFunctional = testcase.Category?.toLowerCase() === "functional testing";
                                    const subCategory = testcase["Sub-Category"]?.toLowerCase() || "";

                                    let prefix = isFunctional ? "TCD_FT" : "TCD_NFT";
                                    let shortCode = "";

                                    if (subCategory.includes("positive")) shortCode = "POS";
                                    else if (subCategory.includes("negative")) shortCode = "NEG";
                                    else if (subCategory.includes("boundary")) shortCode = "BOUND";
                                    else if (subCategory.includes("error")) shortCode = "ERR";
                                    else if (subCategory.includes("security")) shortCode = "SEC";
                                    else if (subCategory.includes("performance")) shortCode = "PERF";

                                    const padded = String(tcCounter).padStart(2, "0");
                                    const id = `${prefix}_${padded}_${shortCode}`;
                                    tcCounter++;

                                    return {
                                        ...testcase,
                                        "Test Case ID": id,
                                    };
                                });

                                finalArray.push(...updated);
                            }
                        }

                        if (selectedTestCases.includes("Resource Chain test case") || selectionMode === "all") {
                            progressHandler.updateMessage("Generating Resource Chain Test Cases");

                            try {
                                const resourceChainResponse = await resourceChainTestCases({
                                    endpoints: responseData.data.item
                                });

                                if (resourceChainResponse?.data?.length) {
                                    finalArray.push(...resourceChainResponse.data);
                                }
                            } catch (chainError) {
                                console.error("Error generating resource chain test cases:", chainError.message);
                            }

                        }
                        allTestCasesEnd.push(...finalArray)





                        await progressHandler.finishAndClose();


                    }
                } catch (error) {
                    console.error("Error generating test cases:", error);
                    setLoadingState({ open: false, message: "", progress: null });
                    setPopupMessage("Something went wrong while generating the test cases.");
                    setPopupOpen(true);
                } finally {
                    if (progressHandler) {
                        progressHandler.stop();
                    }
                    setLoadingState({ open: false, message: "", progress: null });
                }
            }

        }

        if (allTestCasesEnd.length > 0) {
            navigate("/dashboard/api-testing/test-cases-display", {
                state: {
                    ...location.state,
                    generateTestCasesData: allTestCasesEnd,
                    button: "importAPIDefinition",
                    testCasesToGenerate: testCasesToGenerate

                },
            });
        } else {
            console.warn("No test cases were generated");
        }

    }



    return (


        <Box sx={{
            p: 2,
            bgcolor: "#F6F3F2",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            maxHeight: "100%",
            minHeight: 0,
        }}>
            <Box
                sx={{
                    width: "100%",
                    // height: { xs: "60vh", sm: "65vh", md: "73vh" }, // 

                    overflowY: "scroll", // scrolling works
                    scrollbarWidth: "none", // Firefox
                    "&::-webkit-scrollbar": {
                        display: "none", // Chrome, Safari, Edge
                    },  // adds scroll if content is taller
                    // mx: "auto",
                    mb: 1.5
                }}
            >

                <Card
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        p: 2,
                        mb: 1,
                        border: "none"
                    }}
                >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 30 }} >
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, fontSize: 17 }}>
                            Captured API Endpoints
                        </Typography>
                        <TextField
                            InputProps={{
                                startAdornment: (
                                    <SearchIcon style={{ color: "#a9a9a9", marginRight: 8 }} />
                                ),
                            }}
                            placeholder="Search by endpoint"
                            variant="outlined"

                            size="small"
                            sx={{ mb: 1, width: 500 }}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </Box>
                    {/* <Divider sx={{ mb: 2 }} /> */}

                    {/* Empty State */}
                    <Box
                        sx={{
                            textAlign: "center",
                            py: 2,
                            color: "text.secondary",
                            //minHeight: 200,
                            maxHeight: 600,
                            overflowX: "auto",
                            overflowY: "auto",
                            position: "relative",
                            backgroundColor: "#fff",
                            width: '100%'
                            // Ensure background is white
                        }}

                    >
                        {/* <NoDataIcon /> */}

                        {overAllEndpoints && overAllEndpoints.length > 0 && (
                            <TableContainer sx={{ maxHeight: 600 }}>
                                <Table stickyHeader sx={{ border: '1px solid #e0e0e0', borderLeft: 'none', borderRight: 'none', width: '100%' }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    backgroundColor: "#f8f8f8",
                                                    position: "sticky",
                                                    top: 0,
                                                    zIndex: 3,
                                                    margin: 0
                                                }}
                                            ><b>HTTP Method</b>
                                                <ArrowDownwardOutlinedIcon sx={{
                                                    fontSize: "medium", ml: 0.3, position: "relative",
                                                    top: "3px"
                                                }} />
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    backgroundColor: "#f8f8f8",
                                                    position: "sticky",
                                                    top: 0,
                                                    zIndex: 3,
                                                    margin: 0
                                                }}
                                            ><b>Endpoint</b>
                                                <ArrowDownwardOutlinedIcon sx={{
                                                    fontSize: "medium", ml: 0.3, position: "relative",
                                                    top: "3px"
                                                }} />
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    backgroundColor: "#f8f8f8",
                                                    position: "sticky",
                                                    top: 0,
                                                    zIndex: 3,
                                                    margin: 0
                                                }}
                                            ><b>Param Type</b>
                                                <ArrowDownwardOutlinedIcon sx={{
                                                    fontSize: "medium", ml: 0.3, position: "relative",
                                                    top: "3px"
                                                }} />
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    backgroundColor: "#f8f8f8",
                                                    position: "sticky",
                                                    top: 0,
                                                    zIndex: 3,
                                                    margin: 0
                                                }}
                                            ><b>Params</b>
                                                <ArrowDownwardOutlinedIcon sx={{
                                                    fontSize: "medium", ml: 0.3, position: "relative",
                                                    top: "3px"
                                                }} />
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    backgroundColor: "#f8f8f8",
                                                    position: "sticky",
                                                    top: 0,
                                                    zIndex: 3,
                                                    margin: 0
                                                }}
                                            ><b>Headers</b>
                                                <ArrowDownwardOutlinedIcon sx={{
                                                    fontSize: "medium", ml: 0.3, position: "relative",
                                                    top: "3px"
                                                }} />
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    backgroundColor: "#f8f8f8",
                                                    position: "sticky",
                                                    top: 0,
                                                    zIndex: 3,
                                                    margin: 0
                                                }}
                                            ><b>Auth Type</b>
                                                <ArrowDownwardOutlinedIcon sx={{
                                                    fontSize: "medium", ml: 0.3, position: "relative",
                                                    top: "3px"
                                                }} />
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    backgroundColor: "#f8f8f8",
                                                    position: "sticky",
                                                    top: 0,
                                                    zIndex: 3,
                                                    margin: 0
                                                }}
                                            ><b>Body</b>
                                                <ArrowDownwardOutlinedIcon sx={{
                                                    fontSize: "medium", ml: 0.3, position: "relative",
                                                    top: "3px"
                                                }} />
                                            </TableCell>
                                            <TableCell sx={{
                                                position: "sticky",
                                                right: 0,
                                                zIndex: 3,
                                                backgroundColor: "#f8f8f8",
                                                top: 0,
                                                minWidth: 80,
                                                borderLeft: "1px solid #E2E2E2",
                                            }}><b>Actions</b>
                                                <ArrowDownwardOutlinedIcon sx={{
                                                    fontSize: "medium", ml: 0.3, position: "relative",
                                                    top: "3px"
                                                }} />
                                            </TableCell>


                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredRows.map((row, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell sx={{ minWidth: 200 }}>{row.method}</TableCell>
                                                <TableCell sx={{ color: "text.secondary", minWidth: 200 }}>{row.path}</TableCell>
                                                <TableCell sx={{ color: "text.secondary", minWidth: 200 }}>{row.paramType || "-"}</TableCell>
                                                <TableCell sx={{ color: "text.secondary", minWidth: 200 }}>{renderInputData(row.params)}</TableCell>
                                                <TableCell sx={{ color: "text.secondary", minWidth: 200 }}>{renderInputData(row.headers)}</TableCell>
                                                <TableCell sx={{ color: "text.secondary", minWidth: 200 }}>{row.authType}</TableCell>
                                                <TableCell sx={{ color: "text.secondary", minWidth: 200 }}>{renderInputData(row.body)}</TableCell>
                                                <TableCell
                                                    sx={{
                                                        position: "sticky",
                                                        right: 0,
                                                        zIndex: 1,
                                                        backgroundColor: "#fff",
                                                        minWidth: 80,
                                                        textAlign: "center",
                                                        borderLeft: "1px solid #E2E2E2",
                                                    }}
                                                >
                                                    <IconButton
                                                        color="primary"
                                                        // your edit handler
                                                        disabled

                                                        size="small"
                                                    >
                                                        <ModeEditOutlinedIcon />
                                                    </IconButton>
                                                </TableCell>

                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}


                    </Box>
                </Card>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, }}>

                <Button variant="contained" endIcon={< ArrowForwardIcon style={{ marginLeft: 8 }} />} sx={{
                    borderRadius: "8px",
                    backgroundColor: "#0094FB",
                    textTransform: "none",
                    fontWeight: "bold",
                    padding: "4px 16px"


                }}
                    onClick={() => setDrawerOpen(true)}


                >
                    Generate test cases
                </Button>
            </Box>
            <MessagePopup
                modalOpen={popupOpen}
                message={popupMessage}
                okayHandler={() => setPopupOpen(false)}
            />


            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(true)}
                PaperProps={{
                    sx: {
                        width: "40%",
                        bgcolor: '#f8f9fa'
                    }
                }}
            >
                <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', minHeight: "100vh" }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <IconButton onClick={handleBack} sx={{ mr: 1, p: 0.5 }}>
                            <ArrowBack />
                        </IconButton>
                        <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
                            Test Case Selection
                        </Typography>
                        <IconButton onClick={() => setDrawerOpen(false)} sx={{ p: 0.5 }}>
                            <Close />
                        </IconButton>
                    </Box>

                    {/* Selection Options */}
                    <Box sx={{ mb: 1, flex: 1 }}>
                        <RadioGroup
                            value={selectionMode}
                            onChange={handleSelectionModeChange}
                        >
                            <FormControlLabel
                                value="choose"
                                control={<Radio />}
                                label={
                                    <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                                        Choose test cases to be generated
                                    </Typography>
                                }
                                sx={{ mb: 1 }}
                            />

                            {selectionMode === 'choose' && (
                                <Box sx={{ ml: 4, mb: 1 }}>
                                    {/* Functional Test Cases */}
                                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'black', fontWeight: 550, fontSize: 14 }}>
                                        Functional Test Cases
                                    </Typography>
                                    <Box sx={{ mb: 2, display: "flex", flexDirection: "column" }}>
                                        {functionalTestCases.map((testCase) => (
                                            <FormControlLabel
                                                key={testCase.id}
                                                control={
                                                    <Checkbox
                                                        checked={selectedTestCases.includes(testCase.id)}
                                                        onChange={() => handleTestCaseToggle(testCase.id)}
                                                        size="small"
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                        {testCase.label}
                                                    </Typography>
                                                }
                                            // sx={{ mb: 1 }}
                                            />
                                        ))}
                                    </Box>

                                    {/* Non-Functional Test Cases */}
                                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'black', fontWeight: 550, fontSize: 14 }}>
                                        Non-Functional Test Cases
                                    </Typography>
                                    <Box sx={{ mb: 1, display: "flex", flexDirection: "column" }}>
                                        {nonFunctionalTestCases.map((testCase) => (
                                            <FormControlLabel
                                                key={testCase.id}
                                                control={
                                                    <Checkbox
                                                        checked={selectedTestCases.includes(testCase.id)}
                                                        onChange={() => handleTestCaseToggle(testCase.id)}
                                                        size="small"
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                        {testCase.label}
                                                    </Typography>
                                                }

                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            <FormControlLabel
                                value="all"
                                control={<Radio />}
                                label={
                                    <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                                        Generate all test cases
                                    </Typography>
                                }
                            />
                        </RadioGroup>
                    </Box>

                    {/* Footer Buttons */}
                    <Box sx={{
                        display: "flex", justifyContent: "space-between",
                        gap: 2,
                        p: 2,
                        mb: 1,

                    }}>
                        <Button
                            variant="outlined"
                            onClick={handleBack}
                            sx={{
                                borderRadius: "8px", color: "#0094FB", borderColor: " #0094FB",
                                borderWidth: "2px",   // makes border bold
                                fontWeight: "bold", textTransform: "none",
                                padding: "8px 16px",
                                mb: 1,

                            }}
                        >
                            Back
                        </Button>
                        <Button
                            variant="contained"
                            onClick={generateTestcases}
                            disabled={selectionMode === 'choose' && selectedTestCases.length === 0}
                            sx={{
                                borderRadius: "8px",
                                backgroundColor: "#0094FB",
                                textTransform: "none",
                                fontWeight: "bold",
                                padding: "8px 16px",
                                mb: 1,
                            }}
                        >
                            Confirm
                        </Button>
                    </Box>
                </Box>
            </Drawer>

        </Box>
    )
}
export default EndpointsDisplayPage;
